import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const CONTACT_FORM_ID = '51chigua-contact';
const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d96c3rfpg5pqhofcun8g';

export default function ContactCTA() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.email.trim() || !formData.message.trim()) return;

    const formEl = e.currentTarget;
    const formElData = new FormData(formEl);
    const honeypot = formElData.get('phone_alt') as string;
    if (honeypot && honeypot.trim()) {
      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
      return;
    }

    try {
      const params = new URLSearchParams();
      if (formData.name.trim()) params.append('name', formData.name.trim());
      params.append('email', formData.email.trim());
      params.append('message', formData.message.trim());
      const res = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore parse error */ }
      if (res.ok && parsed?.code === 'OK') {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setSubmitStatus('error');
        setFormError(serverMsg || '投稿失败，请稍后重试');
      }
    } catch {
      setSubmitStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  return (
    <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
      <div className="max-w-[1280px] mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-4xl text-foreground-950">
            {t('about_cta_title')}
          </h2>
          <p className="text-sm text-foreground-600 mt-3">{t('about_cta_subtitle')}</p>
        </div>

        {submitStatus === 'success' ? (
          <div className="max-w-[520px] mx-auto text-center">
            <span className="w-14 h-14 mx-auto rounded-full bg-primary-100 flex items-center justify-center mb-4">
              <i className="ri-check-line text-primary-600 text-2xl"></i>
            </span>
            <p className="text-sm text-foreground-700 font-medium">投稿已提交！感谢你的爆料，我们会尽快审核~</p>
          </div>
        ) : (
          <form
            id={CONTACT_FORM_ID}
            data-readdy-form=""
            onSubmit={handleSubmit}
            className="max-w-[520px] mx-auto flex flex-col gap-4"
          >
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="你的昵称（选填）"
              className="w-full px-4 py-3 rounded-xl border border-background-300 bg-background-50 text-foreground-950 text-sm placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 transition-colors"
            />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="你的邮箱"
              className="w-full px-4 py-3 rounded-xl border border-background-300 bg-background-50 text-foreground-950 text-sm placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 transition-colors"
              required
            />
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="请描述你的爆料内容..."
              maxLength={500}
              rows={5}
              className="w-full px-4 py-3 rounded-xl border border-background-300 bg-background-50 text-foreground-950 text-sm placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 transition-colors resize-none"
              required
            ></textarea>
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="form-anti-spam-field"
            />
            <button
              type="submit"
              className="px-7 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
            >
              {t('about_cta_button')}
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-send-plane-line text-base"></i>
              </span>
            </button>
          </form>
        )}
        {submitStatus === 'error' && (
          <p className="text-center text-accent-500 text-xs mt-2">{formError}</p>
        )}
      </div>

      <div className="max-w-[1280px] mx-auto mt-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Newsroom%20team%20collaboration%20scene%2C%20journalists%20working%20on%20laptops%2C%20warm%20lighting%2C%20modern%20office%20interior%2C%20creative%20workspace%2C%20editorial%20environment%2C%20professional%20atmosphere%2C%20natural%20light%20through%20large%20windows&width=600&height=450&seq=about-gallery-01&orientation=landscape"
              alt="51吃瓜编辑部日常 1"
              title="51吃瓜编辑部日常 1"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20newsroom%20brainstorming%20session%2C%20team%20around%20whiteboard%2C%20warm%20office%20lighting%2C%20creative%20collaboration%2C%20startup%20culture%2C%20casual%20professional%20environment&width=600&height=450&seq=about-gallery-02&orientation=landscape"
              alt="51吃瓜编辑部日常 2"
              title="51吃瓜编辑部日常 2"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Journalist%20interviewing%20source%20in%20cozy%20cafe%2C%20notebook%20and%20coffee%2C%20warm%20afternoon%20light%2C%20serious%20conversation%2C%20investigative%20reporting%2C%20professional%20setting&width=600&height=450&seq=about-gallery-03&orientation=landscape"
              alt="51吃瓜编辑部日常 3"
              title="51吃瓜编辑部日常 3"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Late%20night%20editorial%20work%2C%20journalist%20typing%20on%20laptop%20with%20warm%20desk%20lamp%2C%20cozy%20home%20office%2C%20breaking%20news%20deadline%2C%20dedicated%20work%20atmosphere&width=600&height=450&seq=about-gallery-04&orientation=landscape"
              alt="51吃瓜编辑部日常 4"
              title="51吃瓜编辑部日常 4"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </div>
    </section>
  );
}